import React, { createContext, useContext, useEffect, useState } from "react";

// components
import CompletedStepModal from "@/components/Modals/CompletedStepModal";

// helpers
import account from "@/api/query/account";
import useUserProfile from "@/hooks/useUserProfile";

const INITIAL_STATE = {
  open: false,
  type: null,
  spaceSlug: null,
  locationSlug: null,
  campaignId: null,
  progressBar: null,
  onClose: () => null
};

export interface CompletedStepModalContextInterface {
  state: {
    open: boolean;
    type?: string | null;
    spaceSlug?: string | null;
    locationSlug?: string | null;
    campaignId?: number | null;
    progressBar?: number | null;
    onClose?: () => void;
  };
  setState: (newState: Record<string, unknown>) => void;
  resetState: () => void;
}

export const CompletedStepModalContext = createContext<CompletedStepModalContextInterface>({
  state: INITIAL_STATE,
  setState: () => null,
  resetState: () => null,
});

export const useCompletedStepModalContext = () => {
  const context = useContext(CompletedStepModalContext);
  if (!context) {
    throw Error("useCompletedStepModalContext should br used within a CompletedStepModalContext");
  }

  return context;
};

export function withCompletedStepContext<T extends object>(Component: React.ComponentType<T>) {
  return function WrapperComponent(props: T) {
    const [state, setState] = useState(INITIAL_STATE);

    const { mutateAsync } = account.useUpdateProfile();
    const { refetch } = useUserProfile();
    
  
    // methods
    const handleSetState = (newState: Record<string, unknown>) => {
      if (typeof newState !== "undefined") {
        setState(prevState => ({ ...prevState, ...(newState || {}) }));
      }
    };
  
    const resetState = () => setState(INITIAL_STATE);
  
    useEffect(() => {
      if (state.progressBar) {
        mutateAsync({ progressBar: state.progressBar }).then(() => {
          refetch();
        });
      }
    }, [state.progressBar]);

    return (
      <CompletedStepModalContext.Provider value={{ state, setState: handleSetState, resetState }}>
        <Component { ...(props as T) }/>
        <CompletedStepModal
          open={state.open}
          type={state.type}
          spaceSlug={state.spaceSlug}
          locationSlug={state.locationSlug}
          campaignId={state.campaignId}
          handleCloseModal={() => setState(INITIAL_STATE)}
          onClose={state?.onClose as () => void}
        />
      </CompletedStepModalContext.Provider>
    );
  };
}
